import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

// Get the backend URL dynamically - use current origin for deployed apps
const getBackendUrl = () => {
  // If REACT_APP_BACKEND_URL is set and we're in development, use it
  // Otherwise, use the current window origin (for deployed apps)
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Check if we're on a deployed domain (not localhost and not the preview domain)
  if (typeof window !== 'undefined') {
    const currentHost = window.location.host;
    // If we're on the user's deployed domain (not localhost or preview)
    if (!currentHost.includes('localhost') && !currentHost.includes('preview.')) {
      return window.location.origin;
    }
  }
  
  return envUrl || '';
};

export default function ReferenceDetailPage() {
  const { refId } = useParams();
  const navigate = useNavigate();
  const [reference, setReference] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionRanges, setSectionRanges] = useState({});
  
  // Get dynamic backend URL for documents
  const backendUrl = useMemo(() => getBackendUrl(), []);

  useEffect(() => {
    fetchData();
  }, [refId]);

  const fetchData = async () => {
    try {
      const refResponse = await api.get(`/references/${refId}`);
      setReference(refResponse.data);
      
      // For resolutions (like GPPB), fetch sections directly instead of articles
      if (refResponse.data.type === 'resolution') {
        const articlesResponse = await api.get(`/references/${refId}/articles`);
        if (articlesResponse.data.length > 0) {
          const articleId = articlesResponse.data[0].id;
          const sectionsResponse = await api.get(`/articles/${articleId}/sections`);
          setArticles(sectionsResponse.data); // Store sections as articles for display
        }
      } else {
        const articlesResponse = await api.get(`/references/${refId}/articles`);
        setArticles(articlesResponse.data);
        
        // Fetch section ranges for each article
        const ranges = {};
        await Promise.all(
          articlesResponse.data.map(async (article) => {
            try {
              const sectionsResponse = await api.get(`/articles/${article.id}/sections`);
              const sections = sectionsResponse.data;
              if (sections.length > 0) {
                // Extract section numbers (e.g., "Section 1" -> "1", "Section 7" -> "7")
                const sectionNumbers = sections
                  .map(s => {
                    const match = s.number.match(/\d+/);
                    return match ? parseInt(match[0]) : null;
                  })
                  .filter(n => n !== null)
                  .sort((a, b) => a - b);
                
                if (sectionNumbers.length > 0) {
                  const firstSection = sectionNumbers[0];
                  const lastSection = sectionNumbers[sectionNumbers.length - 1];
                  ranges[article.id] = { first: firstSection, last: lastSection };
                }
              }
            } catch (error) {
              ranges[article.id] = null;
            }
          })
        );
        setSectionRanges(ranges);
      }
    } catch (error) {
      toast.error('Failed to load reference details');
    } finally {
      setLoading(false);
    }
  };

  const viewSections = async (articleId) => {
    try {
      // For resolutions, articleId is actually a section ID
      if (reference.type === 'resolution') {
        navigate(`/sections/${articleId}`);
      } else {
        // Navigate to article detail page to show all sections
        navigate(`/articles/${articleId}`);
      }
    } catch (error) {
      toast.error('Failed to load sections');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!reference) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {/* Reference Header */}
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            {reference.short_name}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-4">{reference.title}</p>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
            <span><strong>Effective Date:</strong> {reference.effectivity_date || 'TBA'}</span>
            <span><strong>Status:</strong> <span className="capitalize">{reference.status}</span></span>
          </div>
        </div>

        {/* Search Bar */}
        <SearchBar />

        {/* Articles List */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            {reference.type === 'law' ? 'Articles' : reference.type === 'resolution' ? 'Questions & Answers' : 'Rules'}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            {reference.type === 'law' 
              ? 'Explore the articles that structure this law. Each article is organized into sections containing specific legal provisions'
              : reference.type === 'resolution'
              ? 'Browse frequently asked questions and their detailed answers'
              : 'Navigate through the rules of the Implementing Rules and Regulations (IRR). Each rule contains sections with detailed implementation guidelines'}
          </p>

          {articles.length === 0 ? (
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-8 text-center text-slate-600 dark:text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400 dark:text-slate-600" />
                <p>Content will be available soon. Check back later!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {articles.map((article) => {
                const range = sectionRanges[article.id];
                let sectionRangeText = '';
                if (range) {
                  // If only one section, show "Section X", otherwise "Sections X to Y"
                  if (range.first === range.last) {
                    sectionRangeText = `Section ${range.first}`;
                  } else {
                    sectionRangeText = `Sections ${range.first} to ${range.last}`;
                  }
                }
                
                return (
                  <Card
                    key={article.id}
                    className="hover:shadow-lg transition-all cursor-pointer border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600 dark:bg-slate-800"
                    onClick={() => viewSections(article.id)}
                    data-testid={`article-${article.number.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-emerald-700 dark:text-emerald-400">{article.number}</CardTitle>
                      <CardDescription className="text-base dark:text-slate-400">{article.title}</CardDescription>
                      {sectionRangeText && reference.type !== 'resolution' && (
                        <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                          {sectionRangeText}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                        size="sm"
                        data-testid={`view-article-${article.number.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {reference.type === 'resolution' ? 'View Answers' : 'View Sections'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Latest Issuances Section - Only for RA 9184 IRR */}
        {reference && reference.short_name === 'RA 9184 IRR' && (
          <div className="mt-12 border-t pt-8">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6" style={{ fontFamily: 'IBM Plex Sans' }}>
              Latest Issuances
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Annexes Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg transition-all">
                <CardHeader>
                  <CardTitle className="text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Annexes of 2016 Revised IRR
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    As of 19 July 2024
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Supporting annexes containing templates, forms, and supplementary documentation for the 2016 Revised IRR of RA No. 9184.
                  </p>
                  <div className="flex gap-2">
                    <a 
                      href={`${backendUrl}/api/documents/RA_9184_IRR_Annexes.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                    >
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 flex items-center gap-2"
                        size="sm"
                        asChild
                      >
                        <span>
                          <ExternalLink className="w-4 h-4" />
                          View Online
                        </span>
                      </Button>
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${backendUrl}/api/documents/RA_9184_IRR_Annexes.pdf`;
                        link.download = 'Annexes_2016_Revised_IRR_RA_9184.pdf';
                        link.click();
                        toast.success('Download started');
                      }}
                      className="dark:border-slate-600 dark:text-slate-300 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Appendices Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg transition-all">
                <CardHeader>
                  <CardTitle className="text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Appendices of 2016 Revised IRR
                  </CardTitle>
                  <CardDescription className="dark:text-slate-400">
                    As of 19 July 2024
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Appendices containing detailed guidelines, procedures, and reference materials for the 2016 Revised IRR of RA No. 9184.
                  </p>
                  <div className="flex gap-2">
                    <a 
                      href={`${backendUrl}/api/documents/RA_9184_IRR_Appendices.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                    >
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 flex items-center gap-2"
                        size="sm"
                        asChild
                      >
                        <span>
                          <ExternalLink className="w-4 h-4" />
                          View Online
                        </span>
                      </Button>
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${backendUrl}/api/documents/RA_9184_IRR_Appendices.pdf`;
                        link.download = 'Appendices_2016_Revised_IRR_RA_9184.pdf';
                        link.click();
                        toast.success('Download started');
                      }}
                      className="dark:border-slate-600 dark:text-slate-300 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Information Note */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Note:</strong> These annexes and appendices are official supplementary documents to the 2016 Revised IRR of RA No. 9184, updated as of 19 July 2024. 
                They contain forms, templates, and detailed procedures referenced in the IRR.
              </p>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
